import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Building, Shield, 
  CreditCard, Key, LogOut, Camera, Save, Loader2, ChevronRight,
  Sparkles, CheckCircle2, Lock
} from 'lucide-react';
import { motion } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster'; // Toaster Eklendi
import HeaderActions from '../components/HeaderActions';
import Footer from '../components/Footer';
import { apiService } from '../services/api';

const ProfilePage = () => {
  const { user, logout, updateProfile } = useAuth();
  
  // 🔥 Dil Fonksiyonunu Al
  const { t } = useLanguage(); 
  
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate(); // Navigasyon için eklendi

  const [isLoading, setIsLoading] = useState(false);
  
  // Form verilerini güvenli bir şekilde başlat
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: ''
  });

  // Kullanıcı bilgisi sonradan yüklenirse formu güncelle
  useEffect(() => {
      if (user) {
          setFormData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            address: user.companyDetails?.address || user.address || '',
            company: user.companyDetails?.companyName || user.companyDetails?.name || ''
          });
      }
  }, [user]);

  // --- 1. ONBOARDING VERİSİNİ YAKALA VE KAYDET ---
  useEffect(() => {
    const saveOnboardingData = async () => {
      // Sadece wizard'dan gelindiyse ve onboarding verisi varsa çalışır
      if (location.state?.fromWizard && location.state?.onboardingAnswers) {
        try {
          console.log("Onboarding verisi kaydediliyor...", location.state.onboardingAnswers);
          // Backend'e gönder
          await apiService.auth.updateProfile({
            onboarding: location.state.onboardingAnswers
          });
          
          toast({ 
            title: t('success_general') || "Setup Complete", 
            description: "Your profile has been optimized based on your preferences.", 
            className: "bg-emerald-500 text-white border-0" 
          });
          
          // State'i temizle (Sayfa yenilendiğinde tekrar çalışmasın)
          window.history.replaceState({}, document.title);
          
        } catch (error) {
          console.error("Onboarding save error:", error);
        }
      }
    };
    
    saveOnboardingData();
  }, [location, toast, t]);

  // --- 2. PROFİL GÜNCELLEME ---
  const handleSave = async () => {
    setIsLoading(true);
    try {
        const updatePayload = {
            name: formData.name,
            phone: formData.phone,
            address: formData.address,
            companyDetails: {
                companyName: formData.company,
                // Diğer şirket detaylarını korumak için mevcut user verisini de gönderebiliriz
                // Ancak API tarafı genelde patch (kısmi güncelleme) mantığıyla çalışır.
            }
        };

        await updateProfile(updatePayload); // AuthContext üzerinden güncelle
        
        toast({ 
            title: t('success_general') || "Success", 
            description: t('settings_saved_desc') || "Profile updated successfully.", 
            className: "bg-emerald-500 text-white border-0" 
        });
        
    } catch (error) {
        console.error("Profil güncelleme hatası:", error);
        toast({ 
            variant: "destructive",
            title: t('error_general') || "Error", 
            description: "Update failed. Please try again." 
        });
    } finally {
        setIsLoading(false);
    }
  };

  const handleSecurityAction = (action) => {
      toast({
          title: t('security') || "Security",
          description: `${action} feature will be available soon via email verification.`,
      });
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white font-sans flex flex-col relative overflow-x-hidden">
      <Toaster />
      
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-[#0B1120]/90 backdrop-blur-xl border-b border-white/5 px-6 py-4">
         <div className="max-w-5xl mx-auto flex items-center justify-between">
            <h1 className="text-xl font-black uppercase tracking-tight text-white flex items-center gap-3">
               <User className="text-blue-500" /> {t('menu_profile') || 'Profile'}
            </h1>
            <HeaderActions />
         </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-10 relative z-10">
         
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           
           {/* LEFT: IDENTITY CARD */}
           <motion.div 
             initial={{ opacity: 0, x: -20 }}
             animate={{ opacity: 1, x: 0 }}
             transition={{ duration: 0.5 }}
             className="space-y-6"
           >
               <div className="bg-[#111827] border border-white/5 rounded-[2.5rem] p-8 flex flex-col items-center text-center shadow-xl relative overflow-hidden">
                   <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-600/20 to-transparent"></div>
                   
                   <div className="relative mb-6 group cursor-pointer">
                       <div className="w-28 h-28 rounded-full bg-slate-800 border-4 border-[#0B1120] flex items-center justify-center text-4xl font-black text-slate-500 overflow-hidden shadow-2xl">
                          {user?.photoURL ? <img src={user.photoURL} alt="Profile" className="w-full h-full object-cover"/> : user?.name?.charAt(0)}
                       </div>
                       <div className="absolute bottom-0 right-0 bg-orange-500 p-2 rounded-full border-4 border-[#0B1120] text-white shadow-lg group-hover:scale-110 transition-transform">
                          <Camera size={16} />
                       </div>
                   </div>

                   <h2 className="text-2xl font-black text-white mb-1">{user?.name}</h2>
                   <p className="text-sm text-slate-400 font-medium mb-6">{user?.email}</p>

                   <div className="flex gap-2 w-full">
                       <Button variant="outline" className="flex-1 border-white/10 bg-white/5 hover:bg-white/10 text-white rounded-xl text-xs font-bold h-10">
                          {t('action_edit') || 'Edit Avatar'}
                       </Button>
                       <Button onClick={logout} variant="ghost" className="px-3 border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-xl">
                          <LogOut size={16} />
                       </Button>
                   </div>
               </div>

               {/* ONBOARDING STATUS (Eğer Veri Varsa Göster) */}
               {user?.onboarding && (
                   <div className="bg-[#111827] border border-white/10 rounded-[2rem] p-6 relative overflow-hidden">
                       <div className="flex items-center gap-2 mb-4">
                           <Sparkles size={16} className="text-orange-500" />
                           <h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">{t('goals') || 'Goals'}</h3>
                       </div>
                       <div className="space-y-3">
                           {Object.entries(user.onboarding).map(([key, value]) => (
                               <div key={key} className="flex items-center justify-between text-sm p-2 bg-white/5 rounded-lg border border-white/5">
                                   <span className="text-slate-400 capitalize">{key.replace('_', ' ')}</span>
                                   <span className="font-bold text-white capitalize flex items-center gap-1">
                                       <CheckCircle2 size={12} className="text-emerald-500"/> {value}
                                   </span>
                               </div>
                           ))}
                       </div>
                   </div>
               )}

               {/* SUBSCRIPTION STATUS */}
               <div className="bg-gradient-to-br from-indigo-900 to-blue-900 border border-white/10 rounded-[2rem] p-8 text-white relative overflow-hidden shadow-xl">
                   <div className="absolute top-0 right-0 p-6 opacity-20"><CreditCard size={80} /></div>
                   <p className="text-xs font-bold text-blue-200 uppercase tracking-widest mb-2">{t('current_plan') || 'Current Plan'}</p>
                   <h3 className="text-3xl font-black italic mb-4">{user?.subscriptionPlan === 'premium' ? 'PRO PLAN' : 'STARTER'}</h3>
                   <p className="text-sm text-blue-100/70 mb-6">Valid until: 24.12.2025</p>
                   <Button onClick={() => navigate('/subscription')} className="w-full bg-white text-blue-900 font-bold hover:bg-blue-50 rounded-xl text-xs h-10">
                       {t('action_upgrade') || 'Manage Subscription'}
                   </Button>
               </div>
           </motion.div>

           {/* RIGHT: EDIT FORM */}
           <motion.div 
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.5, delay: 0.2 }}
             className="lg:col-span-2"
           >
               <div className="bg-[#111827] border border-white/5 rounded-[2.5rem] p-8 md:p-10 shadow-xl">
                   <div className="flex items-center justify-between mb-8">
                       <h3 className="text-lg font-black text-white uppercase tracking-wider">{t('menu_profile') || 'Personal Information'}</h3>
                       <Button onClick={handleSave} disabled={isLoading} className="bg-orange-600 hover:bg-orange-500 text-white rounded-xl px-6 font-bold text-xs h-10 shadow-lg shadow-orange-900/20 active:scale-95 transition-all">
                          {isLoading ? <Loader2 className="animate-spin" /> : <><Save size={16} className="mr-2"/> {t('action_save') || 'Save Changes'}</>}
                       </Button>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <div className="space-y-2">
                          <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('label_fullname') || 'Full Name'}</Label>
                          <div className="relative">
                             <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                             <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="pl-12 h-14 bg-[#0B1120] border-white/10 rounded-xl text-white font-medium focus:ring-2 focus:ring-blue-500"/>
                          </div>
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('label_email') || 'Email'}</Label>
                          <div className="relative">
                             <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                             <Input value={formData.email} disabled className="pl-12 h-14 bg-[#0B1120]/50 border-white/5 rounded-xl text-slate-400 font-medium cursor-not-allowed"/>
                          </div>
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('phone') || 'Phone'}</Label>
                          <div className="relative">
                             <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                             <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="pl-12 h-14 bg-[#0B1120] border-white/10 rounded-xl text-white font-medium focus:ring-2 focus:ring-blue-500"/>
                          </div>
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('company') || 'Company'}</Label>
                          <div className="relative">
                             <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                             <Input value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="pl-12 h-14 bg-[#0B1120] border-white/10 rounded-xl text-white font-medium focus:ring-2 focus:ring-blue-500"/>
                          </div>
                       </div>
                       <div className="space-y-2 md:col-span-2">
                          <Label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('address') || 'Address'}</Label>
                          <div className="relative">
                             <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                             <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="pl-12 h-14 bg-[#0B1120] border-white/10 rounded-xl text-white font-medium focus:ring-2 focus:ring-blue-500"/>
                          </div>
                       </div>
                   </div>

                   <div className="mt-10 pt-10 border-t border-white/5">
                       <h3 className="text-lg font-black text-white uppercase tracking-wider mb-6">{t('security') || 'Security'}</h3>
                       <div className="flex flex-col gap-4">
                          <button onClick={() => handleSecurityAction('Password Change')} className="flex items-center justify-between w-full p-4 bg-[#0B1120] hover:bg-white/5 rounded-xl border border-white/5 transition-colors group">
                             <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400 group-hover:text-blue-300"><Key size={20}/></div>
                                <div className="text-left">
                                   <p className="text-sm font-bold text-white">{t('change_password') || 'Change Password'}</p>
                                   <p className="text-xs text-slate-500">Last changed 3 months ago</p>
                                </div>
                             </div>
                             <ChevronRight size={18} className="text-slate-600"/>
                          </button>
                          <button onClick={() => handleSecurityAction('2FA')} className="flex items-center justify-between w-full p-4 bg-[#0B1120] hover:bg-white/5 rounded-xl border border-white/5 transition-colors group">
                             <div className="flex items-center gap-4">
                                <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-400 group-hover:text-emerald-300"><Shield size={20}/></div>
                                <div className="text-left">
                                   <p className="text-sm font-bold text-white">2-Factor Authentication</p>
                                   <p className="text-xs text-emerald-500 font-bold">Enabled</p>
                                </div>
                             </div>
                             <ChevronRight size={18} className="text-slate-600"/>
                          </button>
                       </div>
                   </div>

               </div>
           </motion.div>

        </div>
      </main>
      
      <div className="relative z-10 mt-10">
         <Footer />
      </div>
    </div>
  );
};

export default ProfilePage;